'use client';

import { c as _c } from "react/compiler-runtime";
import './content-feed-card.scss';
import React from 'react';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ContentFeedAuthor, ContentFeedFundingProgress, ContentFeedMedia, ContentFeedQueryMatch } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { trackSelectContent } from '@sola/tracking/components/datalayer.tsx';
import { ContentFeedAvatar } from '../../atoms/content-feed-avatar/content-feed-avatar.tsx';
import { ContentFeedProgressBar } from '../../atoms/content-feed-progress-bar/content-feed-progress-bar.tsx';
import { ContentFeedGallery } from '../../molecules/content-feed-gallery/content-feed-gallery.tsx';
import { useComponentContentFeedCard } from './content-feed-card.logic.ts';
export type { ContentFeedAuthor, ContentFeedFundingProgress, ContentFeedMedia } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
export type ContentFeedMatch = ContentFeedQueryMatch;
export interface ContentFeedCardProps {
  /** Unique entry ID from the API (used for tracking) */
  entryId?: string;
  /** Map of participant localId → preferred name (for banner text resolution) */
  participantNameMap?: Map<string, string>;
  /** Content source type — determines which layout variant to render */
  sourceType?: 'causes' | 'community' | 'compassion.media' | 'causes-progress-updates';
  /** Icon displayed in the top banner */
  icon?: IconNames;
  /** Entry title (resolved for locale) */
  title: string;
  /** Entry description (resolved for locale) — used by community/compassion.media sources */
  description?: string;
  /** Media assets from the entry */
  media?: ContentFeedMedia[];
  /** Funding progress metadata (Causes source only) */
  fundingProgress?: ContentFeedFundingProgress;
  /** Link metadata for the CTA */
  link?: {
    url: string;
  };
  /** Program country ISO code (e.g. "DO", "UG") */
  programCountryIsoCode?: string;
  /** Resolved country display name (looked up from isoCode) */
  programCountryName?: string;
  /** Query match context — drives the banner relationship message */
  match?: ContentFeedMatch;
  /** Custom banner text override. If not provided, derived from match data. */
  bannerText?: string;
  /** Whether to show the "New Update" badge on the first image */
  hasNewUpdate?: boolean;
  /** CTA button label override */
  ctaLabel?: string;
  /** ISO 8601 timestamp of when the entry was published */
  publishedAt?: string;
  /** Author of the entry content (community/compassion.media sources) */
  author?: ContentFeedAuthor;
}
export const ContentFeedCard: React.FC<ContentFeedCardProps> = props => {
  const $ = _c(42);
  const {
    entryId
  } = props;
  const {
    isCausesLayout,
    isPostLayout,
    author,
    authorLine,
    formattedPublishedAt,
    description,
    icon,
    title,
    fundingProgress,
    link,
    programCountryName,
    hasNewUpdate,
    galleryItems,
    totalPhotos,
    resolvedBannerText,
    progressPercent,
    formattedRaised,
    formattedGoal,
    fundedMessage,
    isFundedOrClosed,
    lightboxItems,
    resolvedCtaLabel,
    t
  } = useComponentContentFeedCard(props);
  let t0;
  if ($[0] !== entryId || $[1] !== props.sourceType || $[2] !== title) {
    t0 = () => {
      if (entryId) {
        const type = props.sourceType ? `${props.sourceType}:cta` : "content-feed:cta";
        trackSelectContent(entryId, title, type, [type]);
      }
    };
    $[0] = entryId;
    $[1] = props.sourceType;
    $[2] = title;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleCtaClick = t0;
  let t1;
  if ($[4] !== icon || $[5] !== resolvedBannerText) {
    t1 = resolvedBannerText && <div className="ContentFeedCard__banner flex items-center gap-snug-xl p-comfy-md"><ArkIcon iconName={icon} className="ContentFeedCard__banner-icon label-neutral-mild" size="1.5em" /><span className="label-moderate label-md label-neutral-moderate">{resolvedBannerText}</span></div>;
    $[4] = icon;
    $[5] = resolvedBannerText;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== author || $[8] !== authorLine || $[9] !== formattedPublishedAt || $[10] !== isPostLayout || $[11] !== programCountryName) {
    t2 = isPostLayout && author && <div className="ContentFeedCard__post-header"><div className="flex items-start gap-comfy-sm"><ContentFeedAvatar src={author.photo?.url} alt={author.displayName} /><div className="flex flex-col"><span className="label-potent label-md label-neutral-moderate mb-snug-sm">{authorLine}</span>{programCountryName && <div className="flex items-center gap-snug-sm label-neutral-mild"><ArkIcon iconName="marker-pin-01" size="1em" /><span className="label-sm">{programCountryName}</span></div>}</div></div>{formattedPublishedAt && <span className="label-sm label-neutral-mild flex-shrink-0">{formattedPublishedAt}</span>}</div>;
    $[7] = author;
    $[8] = authorLine;
    $[9] = formattedPublishedAt;
    $[10] = isPostLayout;
    $[11] = programCountryName;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== entryId || $[14] !== galleryItems || $[15] !== hasNewUpdate || $[16] !== lightboxItems || $[17] !== props.sourceType || $[18] !== t || $[19] !== title || $[20] !== totalPhotos) {
    t3 = <ContentFeedGallery galleryItems={galleryItems} lightboxItems={lightboxItems} totalPhotos={totalPhotos} hasNewUpdate={hasNewUpdate} title={title} entryId={entryId} sourceType={props.sourceType} t={t} />;
    $[13] = entryId;
    $[14] = galleryItems;
    $[15] = hasNewUpdate;
    $[16] = lightboxItems;
    $[17] = props.sourceType;
    $[18] = t;
    $[19] = title;
    $[20] = totalPhotos;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== description || $[23] !== formattedGoal || $[24] !== formattedRaised || $[25] !== fundedMessage || $[26] !== fundingProgress || $[27] !== handleCtaClick || $[28] !== isCausesLayout || $[29] !== isFundedOrClosed || $[30] !== link || $[31] !== programCountryName || $[32] !== progressPercent || $[33] !== resolvedCtaLabel || $[34] !== t || $[35] !== title) {
    t4 = isCausesLayout ? <div className="flex flex-col gap-comfy-sm p-comfy-lg pt-0">{programCountryName && <div className="flex items-center gap-snug-sm label-neutral-mild"><ArkIcon iconName="marker-pin-01" size="1.25em" /><span className="label-moderate label-md">{programCountryName}</span></div>}<h3 className="label-lg label-potent">{title}</h3>{fundedMessage ? <p className="label-md label-neutral-mild">{fundedMessage}</p> : fundingProgress && <div className="flex flex-col gap-snug-lg"><ContentFeedProgressBar percent={progressPercent} ariaLabel={t("my:contentFeed.progressLabelA11y", {
          raised: formattedRaised,
          goal: formattedGoal
        })} /><p className="label-moderate label-md label-neutral-mild"><IntlBlock i18nKey="my:contentFeed.progressLabel" values={{
            raised: formattedRaised,
            goal: formattedGoal
          }} /></p></div>}{link?.url && !isFundedOrClosed && <div className="pt-comfy-sm"><ButtonStyle variant="primary" size="lg" fullWidth={true}><Link href={link.url} onClick={handleCtaClick}>{resolvedCtaLabel}</Link></ButtonStyle></div>}</div> : description && <div className="flex flex-col gap-comfy-sm p-comfy-lg pt-0"><p className="label-md label-neutral-moderate ContentFeedCard__description">{description}</p></div>;
    $[22] = description;
    $[23] = formattedGoal;
    $[24] = formattedRaised;
    $[25] = fundedMessage;
    $[26] = fundingProgress;
    $[27] = handleCtaClick;
    $[28] = isCausesLayout;
    $[29] = isFundedOrClosed;
    $[30] = link;
    $[31] = programCountryName;
    $[32] = progressPercent;
    $[33] = resolvedCtaLabel;
    $[34] = t;
    $[35] = title;
    $[36] = t4;
  } else {
    t4 = $[36];
  }
  let t5;
  if ($[37] !== t1 || $[38] !== t2 || $[39] !== t3 || $[40] !== t4) {
    t5 = <article className="ContentFeedCard rounded-lg overflow-hidden">{t1}{t2}{t3}{t4}</article>;
    $[37] = t1;
    $[38] = t2;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
  } else {
    t5 = $[41];
  }
  return t5;
};